import { type FC } from 'react';
import { type FormikProps } from 'formik';
import PasswordChecklist from 'react-password-checklist';
import { type SetupRequestDto } from '../../../models/SetupRequestDto';
import MetronicCard from '@/app/components/layout/MetronicCard';

type Props = {
  formik: FormikProps<SetupRequestDto>;
  passwordConfirmation: string;
  setPasswordConfirmation: (value: string) => void;
  setIsPasswordValid: (value: boolean) => void;
};

const Step2: FC<Props> = ({ formik, passwordConfirmation, setPasswordConfirmation, setIsPasswordValid }) => {
  return (
    <div className="d-flex flex-column w-100">
      <div className="d-flex align-items-center mb-6">
        <h3 className="stepper-title me-7">Configurações de acesso</h3>
        <div className="stepper-desc opacity-50">Configure o acesso administrador</div>
      </div>
      <div className="card h-auto d-flex align-items-center justify-content-center">
        <div className="card-body p-4 p-lg-6 w-100 w-md-50">
          <div className="row g-6 mb-9">
            <div className="col-12">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">E-mail</h3>
              <input
                className="form-control"
                id="administrator_email"
                placeholder="Endereço de e-mail"
                type="text"
                {...formik.getFieldProps('administrator_email')}
              />
              {!!formik.errors.administrator_email && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.administrator_email}</div>
                </div>
              )}
            </div>
          </div>
          <div className="row g-6 mb-9">
            <div className="col-12">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">Senha</h3>
              <input
                className="form-control"
                id="administrator_password"
                placeholder="Senha"
                type="password"
                {...formik.getFieldProps('administrator_password')}
              />
              {!!formik.errors.administrator_password && (
                <div className="fv-plugins-message-container">
                  <div className="fv-help-block">{formik.errors.administrator_password}</div>
                </div>
              )}
            </div>
          </div>
          <div className="row g-6 mb-9">
            <div className="col-12">
              <h3 className="card-title fs-6 mb-4 text-gray-700 text-truncate required">Confirme a senha</h3>
              <input
                className="form-control"
                id="new_password_check"
                placeholder="Repita a nova senha"
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
              />
            </div>
          </div>
          <div className="d-flex flex-column justify-content-center align-items-center my-6">
            <span className="fw-bold text-gray-600 mb-3 fs-6">Sua senha deve conter:</span>
            <PasswordChecklist
              iconSize={12}
              invalidColor="#D12446"
              messages={{
                minLength: 'Mínimo 6 caracteres',
                letter: 'Pelo menos uma letra',
                number: 'Pelo menos um número',
                match: 'As senhas não coincidem',
              }}
              minLength={6}
              rules={['minLength', 'letter', 'number', 'match']}
              validColor="#4BCA81"
              value={formik.values.administrator_password || ''}
              valueAgain={passwordConfirmation}
              onChange={(isValid) => setIsPasswordValid(isValid)}
            />
          </div>
          {/* Dicas para um acesso mais seguro - conteúdo inalterado */}
          <h3 className="stepper-title me-7 mb-6 fs-4">Dicas para um acesso mais seguro</h3>
          <MetronicCard className="h-auto bg-light border border-light">
            <div className="card-body p-4 p-lg-6 d-flex flex-column gap-7">
              <div className="d-flex gap-5 align-items-center">
                <div>
                  <div className="symbol symbol-40px symbol-circle">
                    <div className="symbol-label fs-2 fw-bold bg-white">
                      <i className="bi bi-envelope-check-fill fs-2 text-success" />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="fw-bold text-gray-700">Use um e-mail válido e acessível</div>
                  <div className="text-gray-500">
                    Esse e-mail será usado para recuperar o acesso e receber notificações importantes do sistema.
                  </div>
                </div>
              </div>
              <div className="d-flex gap-5 align-items-center">
                <div>
                  <div className="symbol symbol-40px symbol-circle">
                    <div className="symbol-label fs-2 fw-bold bg-white">
                      <i className="bi bi-shield-lock-fill fs-2 text-success" />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="fw-bold text-gray-700">Escolha uma senha forte</div>
                  <div className="text-gray-500">
                    Crie uma senha com pelo menos 8 caracteres, misturando letras, números e símbolos para aumentar a
                    segurança.
                  </div>
                </div>
              </div>
              <div className="d-flex gap-5 align-items-center">
                <div>
                  <div className="symbol symbol-40px symbol-circle">
                    <div className="symbol-label fs-2 fw-bold bg-white">
                      <i className="bi bi-incognito fs-2 text-success" />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="fw-bold text-gray-700">Mantenha seus dados em segurança</div>
                  <div className="text-gray-500">
                    Não compartilhe seu e-mail e senha com terceiros. Para ainda mais proteção, ative a autenticação em
                    duas etapas nas configurações após o cadastro.
                  </div>
                </div>
              </div>
            </div>
          </MetronicCard>
        </div>
      </div>
    </div>
  );
};

export default Step2;
